import React, { createContext, useState, useContext, useEffect } from 'react';

// Criar o contexto do carrinho
const CartContext = createContext();

// Hook personalizado para usar o contexto do carrinho
export const useCart = () => useContext(CartContext);

// Provedor do contexto do carrinho
export const CartProvider = ({ children }) => {
  // Inicializar o estado do carrinho a partir do localStorage (se existir)
  const [cartItems, setCartItems] = useState(() => {
    const savedCart = localStorage.getItem('cart');
    return savedCart ? JSON.parse(savedCart) : [];
  });
  
  const [cartOpen, setCartOpen] = useState(false);
  
  // Salvar o carrinho no localStorage sempre que ele mudar
  useEffect(() => {
    localStorage.setItem('cart', JSON.stringify(cartItems));
  }, [cartItems]);
  
  // Adicionar um produto ao carrinho
  const addToCart = (product, quantity = 1) => {
    setCartItems(prevItems => {
      // Verificar se o produto já está no carrinho
      const existingItemIndex = prevItems.findIndex(item => item.code === product.code);
      
      if (existingItemIndex >= 0) {
        // Se o produto já estiver no carrinho, atualizar a quantidade
        const updatedItems = [...prevItems];
        updatedItems[existingItemIndex] = {
          ...updatedItems[existingItemIndex],
          quantity: updatedItems[existingItemIndex].quantity + quantity
        };
        return updatedItems;
      } else {
        // Se o produto não estiver no carrinho, adicioná-lo
        return [...prevItems, { ...product, quantity }];
      }
    });
    
    // Abrir o carrinho ao adicionar um produto
    setCartOpen(true);
  };
  
  // Remover um produto do carrinho
  const removeFromCart = (productCode) => {
    setCartItems(prevItems => prevItems.filter(item => item.code !== productCode));
  };
  
  // Atualizar a quantidade de um produto no carrinho
  const updateQuantity = (productCode, quantity) => {
    if (quantity <= 0) {
      removeFromCart(productCode);
      return;
    }
    
    setCartItems(prevItems => 
      prevItems.map(item => 
        item.code === productCode ? { ...item, quantity } : item
      )
    );
  };
  
  // Limpar o carrinho
  const clearCart = () => {
    setCartItems([]);
  };
  
  // Calcular o total de itens no carrinho
  const getTotalItems = () => {
    return cartItems.reduce((total, item) => total + item.quantity, 0);
  };
  
  // Calcular o valor total do carrinho
  const getTotalPrice = () => {
    return cartItems.reduce((total, item) => {
      const price = item.salePrice && item.salePrice < item.price 
        ? item.salePrice 
        : item.price;
      return total + (price * item.quantity);
    }, 0);
  };
  
  // Abrir/fechar o carrinho
  const toggleCart = () => {
    setCartOpen(prevState => !prevState);
  };
  
  // Valores a serem disponibilizados pelo contexto
  const value = {
    cartItems,
    cartOpen,
    addToCart,
    removeFromCart,
    updateQuantity,
    clearCart,
    getTotalItems,
    getTotalPrice,
    toggleCart,
    setCartOpen
  };
  
  return (
    <CartContext.Provider value={value}>
      {children}
    </CartContext.Provider>
  );
};
